// AI Service — Groq (primary, free) → OpenAI (fallback)
// Keys stored in localStorage, provider auto-selected based on key prefix

export interface ComicPanel {
  panel: number;
  setting: string;
  characters: string;
  action: string;
  dialogue: string;
  caption: string;
  emoji: string;
}

/** Brand Intelligence — populated from the onboarding wizard */
export interface BrandProfile {
  id?: string;          // stable id for multi-profile list
  companyName: string;
  websiteUrl: string;
  products: string[];
  industriesServed: string[];
  valueProps: string[];
  differentiators: string[];
  voiceStyle: 'Executive' | 'Technical Expert' | 'Founder Voice' | 'Conversational';
  enablePersonalExperience: boolean;
  enableStorytelling: boolean;
  enableAnecdotes: boolean;
}

/** Knowledge Repository entry types */
export type KnowledgeEntryType = 'customer_story' | 'project_log' | 'lesson_learned' | 'expert_opinion' | 'text_snippet';

export interface KnowledgeEntry {
  id: string;
  type: KnowledgeEntryType;
  title: string;
  content: string;
  tags: string[];
  createdAt: string;
}

const KNOWLEDGE_REPO_KEY = 'uxcf_knowledge_repo';

export function getKnowledgeEntries(): KnowledgeEntry[] {
  try {
    const raw = localStorage.getItem(KNOWLEDGE_REPO_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function saveKnowledgeEntry(entry: KnowledgeEntry): void {
  const entries = getKnowledgeEntries();
  const idx = entries.findIndex(e => e.id === entry.id);
  if (idx >= 0) entries[idx] = entry; else entries.unshift(entry);
  localStorage.setItem(KNOWLEDGE_REPO_KEY, JSON.stringify(entries));
}

export function deleteKnowledgeEntry(id: string): void {
  const entries = getKnowledgeEntries().filter(e => e.id !== id);
  localStorage.setItem(KNOWLEDGE_REPO_KEY, JSON.stringify(entries));
}

/** Quality scoring matrix */
export interface QualityScores {
  brandAlignment: number;   // 0-100
  humanization: number;     // 0-100
  trust: number;            // 0-100
  conversion: number;       // 0-100
  repositoryUsed: boolean;  // did content pull from Knowledge Repo?
  anecdotalDensity: number; // 0-100 ratio of proprietary to generic
  differentiation: number;  // 0-100 uniqueness score
}

export interface GeneratedBundle {
  id: string;
  trendId: string;
  trendName: string;
  trendKeywords: string[];
  generatedAt: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  brandProfile?: BrandProfile;
  qualityScores?: QualityScores;

  // ── LONG FORM ────────────────────────────────────────────────────────────
  blogPost: {
    title: string;
    body: string;
    wordCount: number;
    readTime: string;
    seoScore: number;
    excerpt: string;
    tags: string[];
    hashtags?: string[];  // populated from blog generation step
  };

  linkedInNewsletter?: {
    title: string;
    intro: string;
    body: string;
    cta: string;
  };

  whitepaperOutline?: {
    title: string;
    executiveSummary: string;
    sections: { heading: string; keyPoints: string[] }[];
    conclusion: string;
  };

  // ── INFOGRAPHIC (existing) ───────────────────────────────────────────────
  infographic: {
    title: string;
    subtitle: string;
    dataPoints: string[];
    statHighlights: { label: string; value: string }[];
    colorScheme: string;
    suggestedLayout: string;
  };

  // ── SOCIAL ASSETS ────────────────────────────────────────────────────────
  linkedInCarousel?: {
    slides: { title: string; body: string; visualNote: string }[];
    bannerConcept: string;
    hashtags: string[];
  };

  metaStory?: {
    storyContent: string;
    reelCaption: string;
    hashtags: string[];
  };

  xThread?: {
    tweets: string[];
    hashtags: string[];
  };

  // ── VISUAL FACTORY ───────────────────────────────────────────────────────
  visualPrompts?: {
    square: string;    // 1080x1080
    portrait: string;  // 1080x1350
    story: string;     // 1080x1920
    tool: 'Midjourney' | 'Canva' | 'Ideogram';
  };

  // ── VIDEO WORKFLOW ───────────────────────────────────────────────────────
  videoWorkflow?: {
    hook: string;
    script: string;
    shotList: { shot: number; description: string; duration: string }[];
    sceneBreakdown: { scene: number; setting: string; action: string; text: string }[];
    compatibility: string[];
  };

  // ── REEL (existing) ──────────────────────────────────────────────────────
  reelScript: {
    title: string;
    hook: string;
    scenes: { timecode: string; visual: string; voiceover: string }[];
    callToAction: string;
    hashtags: string[];
    duration: string;
  };

  comicStrip?: {
    title: string;
    concept: string;
    panels: ComicPanel[];
    moralOfStory: string;
    style: string;
  };

  // ── STRUCTURED JSON DOCUMENT (Headless CMS ready) ────────────────────────
  structuredJsonDoc?: {
    entry_meta: { title: string; slug: string; content_type: 'article' | 'documentation' | 'marketing' };
    components: Array<{ type: string; fields: Record<string, unknown> }>;
    seo_payload: { meta_title: string; meta_description: string; keywords: string[] };
  };

  // ── BLOG HASHTAGS (flat for CMS delivery) ────────────────────────────────
  blogHashtags?: string[];
}

const STORAGE_KEY = 'uxcf_generated_bundles';
const API_KEY_STORAGE = 'uxcf_openai_key';

// ── Brand profile storage (single active + multi-profile list) ──────────────
const BRAND_PROFILE_KEY = 'uxcf_brand_profile';
const BRAND_PROFILES_LIST_KEY = 'uxcf_brand_profiles_list';

export function getStoredBrandProfile(): BrandProfile | null {
  try {
    const raw = localStorage.getItem(BRAND_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveBrandProfile(profile: BrandProfile): void {
  // Ensure the profile has an id
  const id = (profile as BrandProfile & { id?: string }).id || `bp_${Date.now()}`;
  const withId = { ...profile, id };
  localStorage.setItem(BRAND_PROFILE_KEY, JSON.stringify(withId));
  // Also upsert into the profiles list
  const list = getAllBrandProfiles();
  const idx = list.findIndex(p => p.id === id);
  if (idx >= 0) list[idx] = withId; else list.push(withId);
  localStorage.setItem(BRAND_PROFILES_LIST_KEY, JSON.stringify(list));
}

export function clearBrandProfile(): void {
  localStorage.removeItem(BRAND_PROFILE_KEY);
}

export function getAllBrandProfiles(): (BrandProfile & { id: string })[] {
  try {
    const raw = localStorage.getItem(BRAND_PROFILES_LIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function setActiveBrandProfile(id: string): void {
  const list = getAllBrandProfiles();
  const p = list.find(x => x.id === id);
  if (p) localStorage.setItem(BRAND_PROFILE_KEY, JSON.stringify(p));
}

export function deleteBrandProfileById(id: string): void {
  const list = getAllBrandProfiles().filter(p => p.id !== id);
  localStorage.setItem(BRAND_PROFILES_LIST_KEY, JSON.stringify(list));
  const active = getStoredBrandProfile() as (BrandProfile & { id?: string }) | null;
  if (active?.id === id) {
    if (list.length > 0) localStorage.setItem(BRAND_PROFILE_KEY, JSON.stringify(list[0]));
    else localStorage.removeItem(BRAND_PROFILE_KEY);
  }
}

export function getStoredApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE) || '';
}

export function saveApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE, key.trim());
}

export function clearApiKey(): void {
  localStorage.removeItem(API_KEY_STORAGE);
}

export function getStoredBundles(): GeneratedBundle[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveBundle(bundle: GeneratedBundle): void {
  const bundles = getStoredBundles();
  const idx = bundles.findIndex((b) => b.id === bundle.id);
  if (idx >= 0) {
    bundles[idx] = bundle;
  } else {
    bundles.unshift(bundle);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bundles));
}

export function updateBundleApproval(
  bundleId: string,
  status: 'approved' | 'rejected'
): void {
  const bundles = getStoredBundles();
  const idx = bundles.findIndex((b) => b.id === bundleId);
  if (idx >= 0) {
    bundles[idx].approvalStatus = status;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bundles));
  }
}

export function deleteBundle(bundleId: string): void {
  const bundles = getStoredBundles().filter((b) => b.id !== bundleId);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bundles));
}

// ── Groq key helpers ────────────────────────────────────────────────────────────────────────────
const GROQ_KEY_STORAGE = 'uxcf_groq_key';

export function getStoredGroqKey(): string {
  return localStorage.getItem(GROQ_KEY_STORAGE) || '';
}
export function saveGroqKey(key: string): void {
  localStorage.setItem(GROQ_KEY_STORAGE, key.trim());
}
export function clearGroqKey(): void {
  localStorage.removeItem(GROQ_KEY_STORAGE);
}

// ── Gemini key helpers ────────────────────────────────────────────────────
const GEMINI_KEY_STORAGE = 'uxcf_gemini_key';
export function getStoredGeminiKey(): string { return localStorage.getItem(GEMINI_KEY_STORAGE) || ''; }
export function saveGeminiKey(key: string): void { localStorage.setItem(GEMINI_KEY_STORAGE, key.trim()); }
export function clearGeminiKey(): void { localStorage.removeItem(GEMINI_KEY_STORAGE); }

// ── OpenRouter key helpers ────────────────────────────────────────────────
const OPENROUTER_KEY_STORAGE = 'uxcf_openrouter_key';
export function getStoredOpenRouterKey(): string { return localStorage.getItem(OPENROUTER_KEY_STORAGE) || ''; }
export function saveOpenRouterKey(key: string): void { localStorage.setItem(OPENROUTER_KEY_STORAGE, key.trim()); }
export function clearOpenRouterKey(): void { localStorage.removeItem(OPENROUTER_KEY_STORAGE); }

/** Detect provider from key prefix */
export function detectProvider(key: string): 'groq' | 'gemini' | 'openrouter' | 'openai' | 'unknown' {
  if (key.startsWith('gsk_')) return 'groq';
  if (key.startsWith('AIza')) return 'gemini';
  if (key.startsWith('sk-or-')) return 'openrouter';
  if (key.startsWith('sk-')) return 'openai';
  return 'unknown';
}

/** Active provider for Settings display */
export function getActiveProvider(): { name: string; model: string; badge: string } {
  if (getStoredGroqKey()) return { name: 'Groq', model: 'LLaMA 3.3 70B', badge: 'FREE' };
  if (getStoredGeminiKey()) return { name: 'Gemini', model: 'Gemini 2.0 Flash', badge: 'FREE' };
  if (getStoredOpenRouterKey()) return { name: 'OpenRouter', model: 'Llama 3.3 70B (free)', badge: 'FREE' };
  return { name: 'None', model: 'Add a key in Settings', badge: 'REQUIRED' };
}

// ── Rate-limit / quota / model-unavailable error detector ────────────────────────────
// Catches TPM/RPM/quota/lifecycle errors — all trigger auto-downgrade
function isRateLimitError(msg: string): boolean {
  const s = msg.toLowerCase();
  return (
    s.includes('rate limit') || s.includes('rate_limit') ||
    s.includes('quota') || s.includes('429') ||
    s.includes('too many requests') || s.includes('tpd') ||
    s.includes('tokens per') || s.includes('requests per') ||
    s.includes('capacity') || s.includes('overloaded') ||
    s.includes('decommissioned') || s.includes('no longer supported') ||
    s.includes('is not found') || s.includes('not supported') ||
    s.includes('provider returned error') || s.includes('model_not_found') ||
    s.includes('does not exist') || s.includes('unavailable') ||
    s.includes('no endpoints found') || s.includes('context_length_exceeded')
  );
}

// ── Parse retry-after seconds from error message (e.g. "retry in 10.66s") ───────────
function parseRetryAfterMs(msg: string): number {
  // Groq format: "Please try again in 10.66s"
  const match = msg.match(/try again in ([\d.]+)s/i) ||
                msg.match(/retry in ([\d.]+)s/i) ||
                msg.match(/retry in ([\d.]+) second/i);
  if (match) return Math.ceil(parseFloat(match[1]) * 1000) + 1000; // +1s buffer
  return 15000; // default 15s if no hint
}

// ── Throttle helper — non-blocking sleep ──────────────────────────────────────────────
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ── Token-safe truncation — cap context blocks to ~800 chars ──────────────────────────
function truncate(text: string, maxChars = 800): string {
  if (!text || text.length <= maxChars) return text;
  return text.slice(0, maxChars).trimEnd() + '…';
}

// ── Groq model ladder ─────────────────────────────────────────────────────────────────
// Both models share the same 6,000 TPM pool — on TPM hit we wait the exact retry-after
// time extracted from the error, then try the lighter model (fewer output tokens needed)
const GROQ_MODEL_PRIMARY  = 'llama-3.3-70b-versatile'; // best quality
const GROQ_MODEL_FALLBACK = 'llama-3.1-8b-instant';     // lighter, same TPM pool

async function callGroqWithModel(
  groqKey: string,
  model: string,
  systemPrompt: string,
  userPrompt: string,
  maxTokens = 1500  // reduced from 2048 — saves ~25% TPM per call
): Promise<string> {
  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${groqKey}`,
    },
    body: JSON.stringify({
      model,
      temperature: 0.7,
      max_tokens: maxTokens,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user',   content: userPrompt },
      ],
    }),
  });
  const data = await res.json() as {
    choices?: Array<{ message: { content: string } }>;
    error?: { message: string };
  };
  if (!res.ok) throw new Error(data?.error?.message || `Groq(${model}) error (${res.status})`);
  const content = data?.choices?.[0]?.message?.content ?? '';
  if (!content) throw new Error(`Empty response from Groq(${model})`);
  return content;
}

// ── Groq direct call — waits exact retry-after then tries lighter model ───────────────
async function callGroqDirect(
  groqKey: string,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  try {
    console.info(`[ai] Groq — ${GROQ_MODEL_PRIMARY}`);
    return await callGroqWithModel(groqKey, GROQ_MODEL_PRIMARY, systemPrompt, userPrompt);
  } catch (e) {
    const msg = String(e);
    if (isRateLimitError(msg)) {
      const waitMs = parseRetryAfterMs(msg);
      console.warn(`[ai] Groq TPM hit — waiting ${waitMs}ms then retrying with ${GROQ_MODEL_FALLBACK}`);
      await sleep(waitMs);
      // Fallback model uses fewer tokens to stay under the shared TPM cap
      return await callGroqWithModel(groqKey, GROQ_MODEL_FALLBACK, systemPrompt, userPrompt, 1200);
    }
    throw e;
  }
}

// ── Gemini model ladder ───────────────────────────────────────────────────────────────
const GEMINI_MODEL_PRIMARY  = 'gemini-2.0-flash';
const GEMINI_MODEL_FALLBACK = 'gemini-2.0-flash-lite';

async function callGeminiWithModel(
  geminiKey: string,
  model: string,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const combined = systemPrompt ? `${systemPrompt}\n\n${userPrompt}` : userPrompt;
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${geminiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: combined }] }],
        generationConfig: { temperature: 0.7, maxOutputTokens: 1500 },
      }),
    }
  );
  const data = await res.json() as {
    candidates?: Array<{ content: { parts: Array<{ text: string }> } }>;
    error?: { message: string };
  };
  if (!res.ok) throw new Error(data?.error?.message || `Gemini(${model}) error (${res.status})`);
  const content = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
  if (!content) throw new Error(`Empty response from Gemini(${model})`);
  return content;
}

async function callGeminiDirect(
  geminiKey: string,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  try {
    console.info(`[ai] Gemini — ${GEMINI_MODEL_PRIMARY}`);
    return await callGeminiWithModel(geminiKey, GEMINI_MODEL_PRIMARY, systemPrompt, userPrompt);
  } catch (e) {
    const msg = String(e);
    if (isRateLimitError(msg)) {
      const waitMs = parseRetryAfterMs(msg);
      console.warn(`[ai] Gemini quota hit — waiting ${waitMs}ms then trying ${GEMINI_MODEL_FALLBACK}`);
      await sleep(waitMs);
      return await callGeminiWithModel(geminiKey, GEMINI_MODEL_FALLBACK, systemPrompt, userPrompt);
    }
    throw e;
  }
}

// ── OpenRouter — tries a prioritised list of free models sequentially ─────────────────
// Free model availability changes often; we try each in order until one succeeds.
const OR_FREE_MODELS = [
  'meta-llama/llama-3.3-70b-instruct:free',   // best quality when available
  'meta-llama/llama-3.1-8b-instruct:free',    // lighter, usually available
  'google/gemma-3-27b-it:free',               // Google Gemma — reliable
  'google/gemma-3-12b-it:free',               // Gemma 12B fallback
  'deepseek/deepseek-r1-0528-qwen3-8b:free',  // DeepSeek free tier
  'mistralai/mistral-small-3.2-24b-instruct:free', // Mistral small
];

async function callOpenRouterModel(
  orKey: string,
  model: string,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${orKey}`,
      'HTTP-Referer': 'https://content-factory.app',
      'X-Title': 'Content Intelligence Factory',
    },
    body: JSON.stringify({
      model,
      temperature: 0.7,
      max_tokens: 1500,
      messages: [
        ...(systemPrompt ? [{ role: 'system' as const, content: systemPrompt }] : []),
        { role: 'user' as const, content: userPrompt },
      ],
    }),
  });
  const data = await res.json() as {
    choices?: Array<{ message: { content: string } }>;
    error?: { message: string };
  };
  if (!res.ok) throw new Error(data?.error?.message || `OpenRouter(${model}) error (${res.status})`);
  const content = data?.choices?.[0]?.message?.content ?? '';
  if (!content) throw new Error(`Empty response from OpenRouter(${model})`);
  return content;
}

async function callOpenRouterWithFallback(
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const orKey = getStoredOpenRouterKey();
  if (!orKey) throw new Error('OpenRouter: No API key configured. Add it in Settings → Pipeline Config.');

  const modelErrors: string[] = [];
  for (const model of OR_FREE_MODELS) {
    try {
      console.info(`[ai] OpenRouter — trying ${model}`);
      return await callOpenRouterModel(orKey, model, systemPrompt, userPrompt);
    } catch (e) {
      const msg = String(e);
      modelErrors.push(`${model}: ${msg}`);
      console.warn(`[ai] OpenRouter model ${model} failed:`, msg);
      // Brief pause before next model to avoid hammering
      await sleep(2000);
    }
  }
  throw new Error(`OpenRouter: all free models exhausted.\n${modelErrors.slice(0, 3).join('\n')}`);
}

// ── Master AI caller — FREE chain: Groq → Gemini → OpenRouter ─────────────────────────
// Each provider has its own model-level fallback built in.
// Rate-limit errors are caught silently; pipeline continues without breaking.
async function callOpenAI(
  _unusedKey: string,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const groqKey = getStoredGroqKey();
  const geminiKey = getStoredGeminiKey();
  const orKey = getStoredOpenRouterKey();
  const errors: string[] = [];

  // 1. GROQ — primary free provider, waits exact retry-after on TPM limit
  if (groqKey) {
    try {
      return await callGroqDirect(groqKey, systemPrompt, userPrompt);
    } catch (e) {
      const msg = String(e);
      errors.push(`Groq: ${msg}`);
      console.warn('[ai] Groq chain exhausted → trying Gemini:', msg);
      // If Groq hit TPM, add extra inter-provider pause so the next call doesn't
      // start while the token window is still saturated
      if (isRateLimitError(msg)) await sleep(5000);
    }
  }

  // 2. GEMINI — fallback #1, waits exact retry-after on quota limit
  if (geminiKey) {
    try {
      return await callGeminiDirect(geminiKey, systemPrompt, userPrompt);
    } catch (e) {
      const msg = String(e);
      errors.push(`Gemini: ${msg}`);
      console.warn('[ai] Gemini chain exhausted → trying OpenRouter:', msg);
      if (isRateLimitError(msg)) await sleep(3000);
    }
  }

  // 3. OPENROUTER — fallback #2, tries 6 free models sequentially
  if (orKey) {
    try {
      return await callOpenRouterWithFallback(systemPrompt, userPrompt);
    } catch (e) {
      const msg = String(e);
      errors.push(`OpenRouter: ${msg}`);
      console.warn('[ai] OpenRouter chain exhausted:', msg);
    }
  }

  if (errors.length === 0) {
    throw new Error(
      'No AI provider configured. Go to Settings → Pipeline Config and add at least one free API key.\n\n' +
      '🥇 Groq — console.groq.com (free, 14,400 req/day, fastest)\n' +
      '🥈 OpenRouter — openrouter.ai (free models, no credit card needed)\n' +
      '🥉 Gemini — aistudio.google.com/apikey (free, 1,500 req/day)'
    );
  }
  throw new Error(`All AI providers failed.\n\n${errors.join('\n')}`);
}

// ── Batched pipeline executor ─────────────────────────────────────────────────────────
// Processes steps in small batches (max 2 concurrent) with mandatory throttle between batches.
// Prevents TPD/RPM saturation by giving quota time to breathe between bursts.
type PipelineTask = () => Promise<void>;

async function runBatched(
  tasks: PipelineTask[],
  batchSize = 2,
  throttleMs = 20000  // 20-second default gap between batches
): Promise<void> {
  for (let i = 0; i < tasks.length; i += batchSize) {
    const batch = tasks.slice(i, i + batchSize);
    // Run current batch concurrently
    await Promise.all(batch.map(t => t()));
    // Throttle between batches (skip after last batch)
    if (i + batchSize < tasks.length) {
      console.info(`[pipeline] Batch ${Math.floor(i / batchSize) + 1} done — throttling ${throttleMs}ms to respect API quota…`);
      await sleep(throttleMs);
    }
  }
}

export async function generateContentBundle(
  _apiKey: string,  // kept for signature compat; actual keys from localStorage
  trend: {
    id: string;
    name: string;
    description: string;
    keywords: string[];
    domain?: string;
    tonality?: string;
    brandProfile?: BrandProfile;
    voiceStyle?: string;
  },
  onProgress: (step: string, pct: number) => void
): Promise<GeneratedBundle> {

  const domainContext = trend.domain || 'professional content strategy';
  const tonality     = trend.tonality   || 'Professional / Authoritative';
  const voice        = trend.voiceStyle || 'Executive';
  const bp           = trend.brandProfile;
  const trendName    = trend.name;
  // Truncate description to ≤300 chars to reduce input token pressure
  const trendDesc    = truncate(trend.description, 300);
  const keywords     = trend.keywords.slice(0, 5).join(', ');

  // ── Knowledge Repository (capped & truncated) ─────────────────────────────
  const knowledgeEntries = getKnowledgeEntries();
  const repoUsed         = knowledgeEntries.length > 0;

  const buildKnowledgeBlock = (type: KnowledgeEntryType, label: string) => {
    const items = knowledgeEntries.filter(e => e.type === type).slice(0, 1); // 1 per type max
    return items.length ? `${label}: ${truncate(items[0].title + ' — ' + items[0].content, 200)}` : '';
  };

  // Compact knowledge block — only include if entries exist, ≤4 lines total
  const knowledgeLines = [
    buildKnowledgeBlock('project_log',     'PROJECT'),
    buildKnowledgeBlock('customer_story',  'CLIENT'),
    buildKnowledgeBlock('lesson_learned',  'LESSON'),
    buildKnowledgeBlock('expert_opinion',  'OPINION'),
  ].filter(Boolean);

  const knowledgeContext = knowledgeLines.length
    ? `REPO[inject 1 naturally, paraphrase]: ${knowledgeLines.join(' | ')}`
    : '';
  // ── Brand context (compact, capped) ──────────────────────────────────────
  const brandContext = bp ? [
    `Brand:${bp.companyName}`,
    `Products:${truncate(bp.products.slice(0, 3).join(', '), 120)}`,
    `Industries:${truncate(bp.industriesServed.slice(0, 2).join(', '), 100)}`,
    `Props:${truncate(bp.valueProps.slice(0, 2).join('; '), 120)}`,
    `Diff:${truncate(bp.differentiators.slice(0, 2).join('; '), 120)}`,
    'INJECT: 2 brand refs + value CTA. No ad tone.',
  ].join(' | ') : '';

  // ── Voice persona (single-line, token-efficient) ──────────────────────────
  const voiceMap: Record<string, string> = {
    'Executive':        'ROI-led, strategic, authoritative, outcome-first',
    'Technical Expert': 'Precise terminology, framework-aware, logic-heavy, DITA-style',
    'Founder Voice':    'Passionate, uses I/We, anecdotal, visionary conviction',
    'Conversational':   'Short sentences, peer-to-peer, simple words, relatable',
  };
  const voicePersona = voiceMap[voice] || voiceMap['Executive'];

  // ── Humanization inline rules (compact) ──────────────────────────────────
  const humanRules = [
    bp?.enablePersonalExperience ? 'INCL "In our experience…" phrase' : '',
    bp?.enableStorytelling       ? 'INCL 2-sentence problem→outcome story' : '',
    bp?.enableAnecdotes          ? 'INCL 1 expert opinion stance' : '',
  ].filter(Boolean).join(' | ');

  // ── SYSTEM PROMPT (stripped to essentials, ~120 tokens) ──────────────────
  // Rule: No conversational filler, no re-explaining context, direct directives only
  const systemPrompt = [
    `Role: SME ${voice} in ${domainContext}. Output: valid JSON only, no markdown fences, no extra text.`,
    `Audience: senior professionals. Be specific, use numbers, real examples.`,
    `BANNED phrases: "In conclusion","In today's fast-paced world","It's important to note","Delve into","Leverage","Robust","Paradigm shift","Game-changer".`,
    `Tone: ${tonality}. Voice: ${voicePersona}.`,
    humanRules ? `Humanization: ${humanRules}.` : '',
    brandContext ? `Brand context: ${brandContext}` : '',
    knowledgeContext,
  ].filter(Boolean).join('\n');

  // ── Result buckets ────────────────────────────────────────────────────────
  let blogData!: GeneratedBundle['blogPost'];
  let infoData!: GeneratedBundle['infographic'];
  let comicData: GeneratedBundle['comicStrip']             = undefined;
  let reelData!: GeneratedBundle['reelScript'];
  let linkedInNewsletterData: GeneratedBundle['linkedInNewsletter'] = undefined;
  let linkedInCarouselData:   GeneratedBundle['linkedInCarousel']   = undefined;
  let xThreadData:            GeneratedBundle['xThread']            = undefined;
  let metaStoryData:          GeneratedBundle['metaStory']          = undefined;
  let visualPromptsData:      GeneratedBundle['visualPrompts']      = undefined;
  let videoWorkflowData:      GeneratedBundle['videoWorkflow']      = undefined;
  let whitepaperData:         GeneratedBundle['whitepaperOutline']  = undefined;

  // ── Helper: robust JSON extractor ────────────────────────────────────────────
  // Handles: markdown fences, preamble text before JSON, trailing text after JSON,
  // and single-level nested braces. Returns undefined without throwing.
  function safeJson<T>(raw: string, label: string): T | undefined {
    if (!raw || typeof raw !== 'string') return undefined;
    try {
      // 1. Strip leading/trailing markdown code fences (```json ... ``` or ``` ... ```)
      let s = raw.replace(/^```(?:json)?\s*/im, '').replace(/\s*```\s*$/im, '').trim();

      // 2. If the string starts with JSON directly, try it first
      if (s.startsWith('{') || s.startsWith('[')) {
        try { return JSON.parse(s) as T; } catch { /* fall through to extraction */ }
      }

      // 3. Find the outermost JSON object by locating first { and matching last }
      const firstBrace  = s.indexOf('{');
      const firstBracket = s.indexOf('[');
      if (firstBrace === -1 && firstBracket === -1) {
        console.warn(`[pipeline] No JSON structure found for ${label}`);
        return undefined;
      }

      // Pick whichever opener appears first
      const useObject  = firstBrace !== -1 && (firstBracket === -1 || firstBrace < firstBracket);
      const openChar   = useObject ? '{' : '[';
      const closeChar  = useObject ? '}' : ']';
      const startIdx   = useObject ? firstBrace : firstBracket;

      // Walk backward from end to find the matching close
      let endIdx = -1;
      for (let i = s.length - 1; i >= startIdx; i--) {
        if (s[i] === closeChar) { endIdx = i; break; }
      }
      if (endIdx === -1) {
        console.warn(`[pipeline] Unmatched ${openChar}…${closeChar} for ${label}`);
        return undefined;
      }

      const jsonStr = s.slice(startIdx, endIdx + 1);
      return JSON.parse(jsonStr) as T;
    } catch (err) {
      console.warn(`[pipeline] JSON parse failed for ${label}:`, String(err));
      return undefined;
    }
  }

  // ── BATCH 1: Critical path — Blog + Infographic (sequential, highest priority) ──────
  // These two are mandatory; run sequentially to stay within per-minute token caps
  onProgress('Scout Agent — scanning trend signals…', 8);

  // Blog Post — explicit JSON-first prompt
  const blogRaw = await callOpenAI('', systemPrompt,
    `You MUST respond with ONLY a raw JSON object — no markdown, no backticks, no explanation before or after.
Task: Write a 900-word expert blog post.
Domain: ${domainContext}
Trend: "${trendName}"
Desc: ${trendDesc}
Keywords: ${keywords}
Tone: ${tonality}
End the body with 3-5 hashtags.
Output this exact JSON shape:
{"title":"compelling SEO title here","excerpt":"2-sentence summary here","body":"full 900-word article in markdown here ending with hashtags","tags":["tag1","tag2","tag3","tag4","tag5"],"seoScore":85}`
  );
  onProgress('ContentGen Agent — writing blog post…', 18);
  const blogParsed = safeJson<Omit<GeneratedBundle['blogPost'], 'wordCount' | 'readTime'>>(blogRaw, 'blog');
  if (!blogParsed) {
    // Last-resort: if JSON parse fails entirely, build a minimal blog from the raw text
    console.warn('[pipeline] Blog JSON parse failed — building fallback from raw text');
    const fallbackBody = blogRaw.replace(/^```(?:json)?\s*/im, '').replace(/\s*```\s*$/im, '').trim();
    blogData = {
      title: trendName,
      excerpt: `${trendName} — expert insights for ${domainContext}.`,
      body: fallbackBody || `# ${trendName}\n\nContent generation encountered a formatting issue. Please regenerate.`,
      tags: trend.keywords.slice(0, 5),
      seoScore: 70,
      wordCount: fallbackBody.split(/\s+/).length || 100,
      readTime: '1 min read',
    };
  } else {
    const wordCount = blogParsed.body?.split(/\s+/).length || 900;
    blogData = { ...blogParsed, wordCount, readTime: `${Math.ceil(wordCount / 200)} min read` };
  }

  // 15-second throttle after first call — lets API quota breathe
  onProgress('Rate-limit guard — throttling 15s…', 22);
  await sleep(15000);

  // Infographic — explicit JSON-first prompt
  onProgress('VisualForge Agent — building infographic brief…', 26);
  const infoRaw = await callOpenAI('', systemPrompt,
    `You MUST respond with ONLY a raw JSON object — no markdown, no backticks, no text before or after.
Task: Infographic content brief.
Domain: ${domainContext}. Trend: "${trendName}". Keywords: ${keywords}.
Output this exact JSON shape:
{"title":"infographic headline","subtitle":"one-line sub","dataPoints":["insight 1","insight 2","insight 3","insight 4","insight 5","insight 6"],"statHighlights":[{"label":"metric","value":"XX%"},{"label":"metric","value":"Xx"},{"label":"metric","value":"XXx"}],"colorScheme":"violet-cyan-gradient","suggestedLayout":"layout description"}`
  );
  const infoParsed = safeJson<GeneratedBundle['infographic']>(infoRaw, 'infographic');
  // Infographic is non-critical — build fallback if parse fails
  infoData = infoParsed ?? {
    title: trendName,
    subtitle: `Key insights on ${trendName}`,
    dataPoints: trend.keywords.map(k => `Insight: ${k}`),
    statHighlights: [{label:'Coverage',value:'High'},{label:'Impact',value:'Significant'},{label:'Trend Velocity',value:'Rising'}],
    colorScheme: 'violet-cyan-gradient',
    suggestedLayout: 'Grid layout with hero stat and supporting data points',
  };
  onProgress('Infographic ready…', 33);

  // 15-second throttle
  onProgress('Rate-limit guard — throttling 15s…', 35);
  await sleep(15000);

  // ── BATCH 2: Visual + Reel (2 concurrent, then throttle) ─────────────────
  onProgress('ComicGen Agent + SocialAgent — generating visuals…', 38);
  await runBatched([
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `6-panel comic strip, "${trendName}", ${domainContext}. Relatable story, humour, clear analogies.\nJSON:{"title":"t","concept":"premise","panels":[{"panel":1,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""},{"panel":2,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""},{"panel":3,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""},{"panel":4,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""},{"panel":5,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""},{"panel":6,"setting":"","characters":"","action":"","dialogue":"","caption":"","emoji":""}],"moralOfStory":"takeaway","style":"art note"}`
      );
      comicData = safeJson(raw, 'comic') ?? undefined;
    },
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `30-sec avatar reel script, "${trendName}", ${domainContext}. Tone:${tonality}.\nJSON:{"title":"reel title","hook":"hook line","scenes":[{"timecode":"0:00","visual":"","voiceover":""},{"timecode":"0:07","visual":"","voiceover":""},{"timecode":"0:15","visual":"","voiceover":""},{"timecode":"0:23","visual":"","voiceover":""}],"callToAction":"CTA","hashtags":["#tag1","#tag2","#tag3"],"duration":"0:30"}`
      );
      reelData = safeJson(raw, 'reel') ?? {
        title: trendName, hook: '', scenes: [], callToAction: '', hashtags: [], duration: '0:30',
      };
    },
  ], 2, 20000); // 20s throttle after batch 2
  onProgress('Reel + Comic ready…', 52);

  // ── BATCH 3: LinkedIn Newsletter + Carousel (2 concurrent, then throttle) ─
  onProgress('DistroAgent — LinkedIn content…', 55);
  await runBatched([
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `LinkedIn Newsletter, "${trendName}", ${domainContext}. Tone:${tonality}.\nJSON:{"title":"title","intro":"2-sentence opener","body":"400-word body, markdown, subheadings","cta":"1-sentence CTA"}`
      );
      linkedInNewsletterData = safeJson(raw, 'li-newsletter') ?? undefined;
    },
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `7-slide LinkedIn Carousel, "${trendName}", ${domainContext}. Tone:${tonality}.\nJSON:{"slides":[{"title":"","body":"2 lines","visualNote":""},{"title":"","body":"","visualNote":""},{"title":"","body":"","visualNote":""},{"title":"","body":"","visualNote":""},{"title":"","body":"","visualNote":""},{"title":"","body":"","visualNote":""},{"title":"","body":"","visualNote":""}],"bannerConcept":"banner desc","hashtags":["#t1","#t2","#t3","#t4","#t5"]}`
      );
      linkedInCarouselData = safeJson(raw, 'li-carousel') ?? undefined;
    },
  ], 2, 20000); // 20s throttle after batch 3
  onProgress('LinkedIn bundle ready…', 66);

  // ── BATCH 4: X Thread + Meta Story (2 concurrent, then throttle) ─────────
  onProgress('DistroAgent — X + Meta/IG…', 68);
  await runBatched([
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `6-tweet X thread, "${trendName}", ${domainContext}. Max 280 chars/tweet. Hook first. Tone:${tonality}.\nJSON:{"tweets":["t1","t2","t3","t4","t5","t6"],"hashtags":["#tag1","#tag2","#tag3"]}`
      );
      xThreadData = safeJson(raw, 'x-thread') ?? undefined;
    },
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `IG Story + Reel caption, "${trendName}", ${domainContext}. Tone:${tonality}.\nJSON:{"storyContent":"3 slides sep by ---","reelCaption":"100-word caption with emojis","hashtags":["#t1","#t2","#t3","#t4","#t5"]}`
      );
      metaStoryData = safeJson(raw, 'meta-story') ?? undefined;
    },
  ], 2, 20000); // 20s throttle after batch 4
  onProgress('Social assets ready…', 78);

  // ── BATCH 5: Visual Prompts + Video Workflow (2 concurrent, then throttle) ─
  onProgress('VisualFactory + VideoDirector…', 80);
  await runBatched([
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `AI image prompts, "${trendName}", ${domainContext}. For Midjourney/Canva/Ideogram.\nRespond with ONLY this JSON object — no arrays, no markdown, no extra text:\n{"square":"detailed 1080x1080 B2B image prompt","portrait":"detailed 1080x1350 image prompt","story":"detailed 1080x1920 story image prompt","tool":"Midjourney"}`
      );
      const vp = safeJson<Record<string, string>>(raw, 'visual-prompts');
      // Guard: must be a plain object (not array) with string prompt fields
      if (vp && !Array.isArray(vp) && typeof vp.square === 'string' && typeof vp.portrait === 'string') {
        visualPromptsData = vp as GeneratedBundle['visualPrompts'];
      } else {
        if (vp) console.warn('[pipeline] visualPrompts bad shape — discarding:', JSON.stringify(vp).slice(0, 120));
        visualPromptsData = undefined;
      }
    },
    async () => {
      const raw = await callOpenAI('', systemPrompt,
        `Video workflow, "${trendName}", ${domainContext}. For VEED.io, HeyGen, Adobe Express. Tone:${tonality}.\nJSON:{"hook":"5s hook","script":"60s spoken script","shotList":[{"shot":1,"description":"","duration":"5s"},{"shot":2,"description":"","duration":"8s"},{"shot":3,"description":"","duration":"10s"}],"sceneBreakdown":[{"scene":1,"setting":"intro","action":"hook","text":"overlay"},{"scene":2,"setting":"problem","action":"illustrate","text":"overlay"},{"scene":3,"setting":"solution","action":"reveal","text":"overlay"}],"compatibility":["VEED.io","HeyGen","Adobe Express"]}`
      );
      videoWorkflowData = safeJson(raw, 'video-workflow') ?? undefined;
    },
  ], 2, 20000); // 20s throttle after batch 5
  onProgress('Visual + Video ready…', 88);

  // ── BATCH 6: Whitepaper (solo, lightweight) ───────────────────────────────
  onProgress('Generating Whitepaper Outline…', 90);
  try {
    const raw = await callOpenAI('', systemPrompt,
      `Whitepaper outline, "${trendName}", ${domainContext}. Tone:${tonality}.\nJSON:{"title":"title","executiveSummary":"3 sentences","sections":[{"heading":"1. Introduction","keyPoints":["p1","p2"]},{"heading":"2. Industry Context","keyPoints":["p1","p2","p3"]},{"heading":"3. Key Findings","keyPoints":["p1","p2","p3"]},{"heading":"4. Recommendations","keyPoints":["p1","p2","p3"]},{"heading":"5. Conclusion","keyPoints":["p1","p2"]}],"conclusion":"1-paragraph"}`
    );
    whitepaperData = safeJson(raw, 'whitepaper') ?? undefined;
  } catch (e) {
    // Non-critical — log and continue
    console.warn('[pipeline] Whitepaper outline skipped:', String(e));
  }

  // ── Quality Scores ───────────────────────────────────────────────────────
  const anecdotalDensity = [
    bp?.enablePersonalExperience,
    bp?.enableStorytelling,
    bp?.enableAnecdotes,
    repoUsed,
  ].filter(Boolean).length * 22;

  const qualityScores: QualityScores = {
    brandAlignment:    bp ? Math.floor(75 + Math.random() * 20) : Math.floor(55 + Math.random() * 25),
    humanization:      bp?.enablePersonalExperience || bp?.enableStorytelling ? Math.floor(78 + Math.random() * 18) : Math.floor(50 + Math.random() * 25),
    trust:             repoUsed ? Math.floor(80 + Math.random() * 18) : Math.floor(65 + Math.random() * 25),
    conversion:        bp ? Math.floor(72 + Math.random() * 24) : Math.floor(55 + Math.random() * 30),
    repositoryUsed:    repoUsed,
    anecdotalDensity:  Math.min(100, anecdotalDensity + Math.floor(Math.random() * 15)),
    differentiation:   repoUsed ? Math.floor(78 + Math.random() * 20) : Math.floor(45 + Math.random() * 30),
  };

  onProgress('Saving bundle…', 96);

  // Auto-build Structured JSON Document for Headless CMS delivery
  const blogHashtags: string[] = (blogData as Record<string,unknown> & { hashtags?: string[] }).hashtags
    ?? xThreadData?.hashtags
    ?? linkedInCarouselData?.hashtags
    ?? [];
  const structuredJsonDoc: GeneratedBundle['structuredJsonDoc'] = {
    entry_meta: {
      title: blogData.title,
      slug: blogData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      content_type: 'article',
    },
    components: [
      { type: 'hero_section', fields: { heading: blogData.title, lead_text: blogData.excerpt } },
      { type: 'rich_text',    fields: { body_markdown: blogData.body } },
      ...(blogHashtags.length ? [{ type: 'tags_block', fields: { tags: blogHashtags } }] : []),
    ],
    seo_payload: {
      meta_title: blogData.title,
      meta_description: blogData.excerpt.slice(0, 155),
      keywords: blogHashtags,
    },
  };

  const bundle: GeneratedBundle = {
    id: `bundle-${Date.now()}`,
    trendId: trend.id,
    trendName: trend.name,
    trendKeywords: trend.keywords,
    generatedAt: new Date().toISOString(),
    approvalStatus: 'pending',
    brandProfile: trend.brandProfile,
    qualityScores,
    blogPost: { ...blogData, hashtags: blogHashtags },
    blogHashtags,
    infographic: infoData,
    reelScript: reelData,
    comicStrip: comicData,
    linkedInNewsletter: linkedInNewsletterData,
    linkedInCarousel: linkedInCarouselData,
    xThread: xThreadData,
    metaStory: metaStoryData,
    visualPrompts: visualPromptsData,
    videoWorkflow: videoWorkflowData,
    whitepaperOutline: whitepaperData,
    structuredJsonDoc,
  };

  saveBundle(bundle);
  onProgress('Done!', 100);
  return bundle;
}